/**
 * SafeIcon — defensive wrapper for lucide icons (or any icon
 * component). Renders a placeholder div when the passed component
 * is undefined / null instead of crashing the entire tree with
 * a `ReferenceError: X is not defined` or
 * `Element type is invalid` runtime error.
 *
 * Why: a single missing `import` (e.g. forgetting to add a new
 * lucide icon to the import block) used to white-screen the whole
 * app on first render. Iter-118.1 added a global RouteErrorBoundary
 * to contain the blast radius; this component keeps the affected
 * tile/button rendering normally so the user never sees the broken
 * surface at all.
 *
 * Usage:
 *   <SafeIcon as={ShoppingBag} size={14} className="text-cyan-300" />
 *
 * If `as` is undefined, a dimensionally matched <span> with a faint
 * border placeholder takes its place.
 */

const DEFAULT_DIMENSION = 16;

export default function SafeIcon({
    as: IconComponent,
    fallback = null,
    size = DEFAULT_DIMENSION,
    className = "",
    style,
    ...rest
}) {
    // Accept React components (functions / forwardRef objects / classes).
    // Reject everything else so a stray string / number / undefined
    // can never reach the JSX renderer.
    const isRenderable =
        typeof IconComponent === "function" ||
        (typeof IconComponent === "object" && IconComponent !== null);

    if (!isRenderable) {
        if (fallback !== null) return fallback;
        const dim =
            typeof size === "number"
                ? `${size}px`
                : typeof size === "string"
                    ? size
                    : `${DEFAULT_DIMENSION}px`;
        return (
            <span
                aria-hidden="true"
                data-testid="safe-icon-fallback"
                className={`inline-block rounded-sm border border-current opacity-30 ${className}`}
                style={{ width: dim, height: dim, ...style }}
            />
        );
    }

    return <IconComponent size={size} className={className} style={style} {...rest} />;
}
